import React from 'react';
import {
    BOARD_MODES,
    CLAIM_DRAG_TYPE,
    ITEM_DRAG_TYPE,
    SERVICE_DRAG_TYPE,
    TRAY_DROPPABLE_ID,
    UNASSIGNED_TITLE,
} from '../constants';
import { trayStatus } from '../utils';
import { ColumnHeader, ColumnMeta, ColumnName, ColumnTitleRow, TrayMessage } from './blocks';
import { DropColumn } from './DropColumn';

const TRAY_DRAG_TYPES = {
    [BOARD_MODES.ITEMS]: ITEM_DRAG_TYPE,
    [BOARD_MODES.SERVICES]: SERVICE_DRAG_TYPE,
    [BOARD_MODES.CLAIMS]: CLAIM_DRAG_TYPE,
};

/**
 * The Unassigned tray at the left of the split board, and a drop target that unassigns a card. Its header counts
 * the cards left in the current mode and prompts the user to place them, or says they're all assigned.
 * `children` are the tray's cards for that mode, and it accepts only that mode's drag type.
 *
 * @param {{ layout: import('../utils').SplitLayout, mode?: 'items'|'services'|'claims', services?: Array<{ service_id: string }>, claims?: Array<{ claim_id: string }>, disabled?: boolean, children?: React.ReactNode }} props
 *   `services` and `claims` are the parent's, counted in their modes. `disabled` stops drops.
 */
export const UnassignedTray = ({
    layout,
    mode = BOARD_MODES.ITEMS,
    services = [],
    claims = [],
    disabled = false,
    children,
}) => {
    const { countText, message, clear } = trayStatus(layout, mode, services, claims);
    return (
        <DropColumn
            droppableId={TRAY_DROPPABLE_ID}
            type={TRAY_DRAG_TYPES[mode] || ITEM_DRAG_TYPE}
            disabled={disabled}
            tray
            clear={clear}
            aria-label={UNASSIGNED_TITLE}
            data-testid="split-board-tray"
            header={
                <ColumnHeader>
                    <ColumnTitleRow>
                        <ColumnName>{UNASSIGNED_TITLE}</ColumnName>
                    </ColumnTitleRow>
                    <ColumnMeta>{countText}</ColumnMeta>
                </ColumnHeader>
            }
            footer={
                <TrayMessage clear={clear} role="status">
                    {message}
                </TrayMessage>
            }
        >
            {children}
        </DropColumn>
    );
};

export default UnassignedTray;
